/**
 * Model used to hold location data. Most events will
 * have the related googlePlaceId filled so we can get even more detials
 * by querying the Google Places API
 */

export interface IAddressComponent {
    long_name: string;
    short_name: string;
    types: string[];
}

export class Location {
    public googlePlaceId: string = null;
    public name: string = null;
    public address: string = null;
    public city: string = null;
    public state: string = null;
    public country: string = null;
    public longitude: number = null;
    public latitude: number = null;
    public zipCode: string = null;
    public timeZone: string = null;

    /**
     * Constructor that can accept a googlePlace json blob
     */
    constructor(googlePlaceJson?: any) {
        if (googlePlaceJson) {
            this.fillFromGooglePlace(googlePlaceJson);
        }
    }

    public format(includeZip: boolean = false): string {
        const parts: string[] = [];

        if (this.city) {
            parts.push(this.city);
        } else if (this.name) {
            parts.push(this.name);
        }

        if (this.state) {
            parts.push(this.state);
        }

        if (includeZip && this.zipCode) {
            parts.push(this.zipCode);
        }

        return parts.join(', ');
    }

    public isEmpty(): boolean {
        return !(this.googlePlaceId ||
            this.name ||
            this.address ||
            this.city ||
            this.state ||
            this.country ||
            this.longitude ||
            this.latitude ||
            this.zipCode ||
            this.timeZone);
    }

    public clone(): Location {
        const clone: Location = new Location();
        clone.googlePlaceId = this.googlePlaceId;
        clone.address = this.address;
        clone.name = this.name;
        clone.city = this.city;
        clone.state = this.state;
        clone.country = this.country;
        clone.longitude = this.longitude;
        clone.latitude = this.latitude;
        clone.zipCode = this.zipCode;
        clone.timeZone = this.timeZone;

        return clone;
    }

    /**
     * Serialize model
     */
    public serialize(): string {
        const json: any = {};
        json.name = this.name;
        json.address = this.address;
        json.googlePlaceId = this.googlePlaceId;
        json.city = this.city;
        json.state = this.state;
        json.country = this.country;
        json.longitude = this.longitude;
        json.latitude = this.latitude;
        json.zipCode = this.zipCode;
        json.timeZone = this.timeZone;

        return json;
    }

    /**
     * Deserialize model
     */
    public deserialize(json: any): Location {
        if (json) {
            this.googlePlaceId = json['googlePlaceId'];
            this.name = json['name'];
            this.address = json['address'];
            this.city = json['city'];
            this.state = json['state'];
            this.country = json['country'];
            this.longitude = json['longitude'];
            this.latitude = json['latitude'];
            this.zipCode = json['zipCode'];
            this.timeZone = json['timeZone'];
        }

        return this;
    }

    /**
     * Fill the Location model from a googlePlace json blob
     */
    public fillFromGooglePlace(googlePlaceJson: any): Location {
        if (googlePlaceJson) {
            this.googlePlaceId = googlePlaceJson.place_id;
            this.name = googlePlaceJson.name;
            this.latitude = null;
            this.longitude = null;
            this.address = null;

            if (googlePlaceJson.geometry &&
                googlePlaceJson.geometry.location) {
                this.latitude = googlePlaceJson.geometry.location.lat();
                this.longitude = googlePlaceJson.geometry.location.lng();
            }

            const streetNum: IAddressComponent = this.findAddressComponent('street_number', googlePlaceJson);
            const route: IAddressComponent = this.findAddressComponent('route', googlePlaceJson);
            if (streetNum.long_name && route.long_name) {
                this.address = `${streetNum.long_name} ${route.long_name}`.trim();
            }

            const city: IAddressComponent = this.findAddressComponent('locality', googlePlaceJson);
            this.city = city.long_name;

            const state: IAddressComponent = this.findAddressComponent('administrative_area_level_1', googlePlaceJson);
            this.state = state.short_name;

            const country: IAddressComponent = this.findAddressComponent('country', googlePlaceJson);
            this.country = country.short_name;

            const zipCode: IAddressComponent = this.findAddressComponent('postal_code', googlePlaceJson);
            this.zipCode = zipCode.long_name;

            // If the name of the address is set to the zip code change it to the city name
            if (this.zipCode === this.name &&
                this.city) {
                this.name = this.city;
            }
        } else {
            this.name = null;
            this.address = null;
            this.googlePlaceId = null;
            this.name = null;
            this.city = null;
            this.state = null;
            this.country = null;
            this.longitude = null;
            this.latitude = null;
            this.zipCode = null;
        }

        return this;
    }

    /**
     * Finds a specified address component from a google places json blob
     */
    public findAddressComponent(componentName: string, googlePlaceJson: any): IAddressComponent {
        if (googlePlaceJson.address_components) {
            for (let i: number = 0; i < googlePlaceJson.address_components.length; i++) {
                if (googlePlaceJson.address_components[i].types[0] === componentName) {
                    // this is the object we are looking for
                    return googlePlaceJson.address_components[i];
                }
            }
        }

        return { long_name: null, short_name: null, types: [] };
    }
}
